import { Card, CardBody, Input, Tooltip } from '@hawkins/components';
import { useState, useEffect } from 'react';
import { ceiling } from 'src/domains/calculators/utilities/ceiling';
import { HorizontalInputStack } from './HorizontalInputStack';
import styles from './CustomResolutionInput.module.css';
import { ResolutionInput } from '../FramingChart/types';

type CustomResolutionInputProps = {
  customHeight: number | null;
  customWidth: number | null;
  onChange: ({ width, height }: ResolutionInput) => void;
};

/**
 * CustomResolutionInput component allows users to input custom width and height values.
 * Used for custom camera sensor resolutions and effective canvase resolutions.
 * Values are rounded up to the nearest even number on blur.
 */
export const CustomResolutionInput = ({
  customHeight,
  customWidth,
  onChange,
}: CustomResolutionInputProps) => {
  const [inputWidth, setInputWidth] = useState(
    customWidth?.toString() ?? ''
  );
  const [inputHeight, setInputHeight] = useState(
    customHeight?.toString() ?? ''
  );

  const [invalidWidth, setInvalidWidth] = useState(false);
  const [invalidHeight, setInvalidHeight] = useState(false);

  useEffect(() => {
    setInputWidth(customWidth?.toString() ?? '');
  }, [customWidth]);

  useEffect(() => {
    setInputHeight(customHeight?.toString() ?? '');
  }, [customHeight]);

  const roundingMessage =
    'Value is rounded up to the nearest even number';

  return (
    <Card>
      <CardBody className={styles.customResolutionInputContainer}>
        <HorizontalInputStack>
          <Tooltip label={roundingMessage} placement="bottom">
            <Input
              validationStatus={invalidWidth ? 'error' : undefined}
              validationMessage={
                invalidWidth ? 'Invalid width' : undefined
              }
              label="Width"
              type="number"
              fullWidth
              value={inputWidth}
              onChange={(event) => {
                const value = event.target.value;
                setInputWidth(value);
                // Set validation state for input
                const width = Number(value);
                setInvalidWidth(value === '' || width <= 0);
              }}
              onBlur={() => {
                const rawWidth = Number(inputWidth);
                if (rawWidth > 0) {
                  const roundedWidth = ceiling(rawWidth, 2);
                  setInputWidth(roundedWidth.toString());
                  onChange({
                    height: customHeight, // keep height unchanged
                    width: roundedWidth,
                  });
                }
              }}
            />
          </Tooltip>
          <Tooltip label={roundingMessage} placement="bottom">
            <Input
              validationStatus={invalidHeight ? 'error' : undefined}
              validationMessage={
                invalidHeight ? 'Invalid height' : undefined
              }
              label="Height"
              type="number"
              fullWidth
              value={inputHeight}
              onChange={(event) => {
                const value = event.target.value;
                setInputHeight(value);
                // Set validation state for input
                const height = Number(value);
                setInvalidHeight(value === '' || height <= 0);
              }}
              onBlur={() => {
                const rawHeight = Number(inputHeight);
                if (rawHeight > 0) {
                  const roundedHeight = ceiling(rawHeight, 2);
                  setInputHeight(roundedHeight.toString());
                  onChange({
                    height: roundedHeight,
                    width: customWidth, // keep width unchanged
                  });
                }
              }}
            />
          </Tooltip>
        </HorizontalInputStack>
      </CardBody>
    </Card>
  );
};
